/**
 * <GeneratedActivityCard> — un énoncé proposé (molecule).
 *
 * Une des trois cartes de `tutor/students/components/MaterialResultsCards.js`, dans son ordre :
 * le chapitre, la ligne des compétences travaillées, les chips (type puis variation), le titre,
 * l'énoncé, l'option de quiz de prérequis, et le bouton qui crée l'activité.
 *
 * Trois états, comme le legacy : « Génération en cours... », « Erreur de génération », et
 * l'énoncé. En cours, la carte montre la forme de l'énoncé qui arrive
 * (`<GeneratedActivityCardSkeleton>`) ; les cartes arrivent chacune à leur rythme. Le bouton dit
 * son avancement — « Créer l'activité » → « Création... » → « Activité ajoutée » — et ne repart
 * pas une fois l'activité créée.
 *
 * `compact` (weekly du 03/11, W10) : la version courte du fil du live. Les compétences tombent, l'énoncé se
 * coupe à quelques lignes et s'efface en fondu vers la carte (jamais une ligne tranchée au milieu des lettres :
 * la police de l'énoncé n'a pas la hauteur de ligne du texte), et les gestes se rangent côte à côte : on la donne à l'élève sans tout lire, et
 * « Voir en grand » (le fil) la relit entière.
 *
 * L'énoncé passe par `<RichText>`, comme celui de la fiche (`<ActivityStatement>`) : ce que Sam
 * propose est du markdown avec des formules, et le parent doit lire sur la carte exactement ce
 * qu'il lira une fois l'activité créée — une fraction composée, pas `$\frac{3}{4}$`.
 */
import { LinearGradient } from "expo-linear-gradient"
import React, { useState } from "react"
import { View } from "react-native"

import { type UIGeneratedActivity } from "../../../ports/primary/ui/types"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Switch } from "../../atoms/Switch"
import { Banner } from "../../atoms/Banner"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../Card"
import { RichText } from "../RichText"
import { GeneratedActivityCardSkeleton } from "./GeneratedActivityCardSkeleton"
import { StyleSheet, useUnistyles } from "react-native-unistyles"
import { hexWithAlpha } from "../../utils/shadow"
import { type Voice } from "../../utils/voice"

/** Le legacy vouvoie tout le monde ici (`MaterialResultsCards.js`) ; l'élève, lui, est tutoyé. */
const TEXTS: Record<Voice, { quizError: string }> = {
    parent: { quizError: "Erreur lors de la génération du quiz. Réessayez ou désactivez l'option quiz." },
    student: { quizError: "Erreur lors de la génération du quiz. Réessaie ou désactive l'option quiz." },
}

export type GeneratedActivityCardProps = {
    activity: UIGeneratedActivity
    /** L'option « Quiz de prérequis » est proposée. Défaut `false` : la ligne disparaît. */
    quizAvailable?: boolean
    onToggleQuiz?: (next: boolean) => void
    onCreate?: () => void
    /**
     * Un second geste sous « Créer l'activité », sur un énoncé prêt (le live du masteur : « Montrer à l'élève »).
     * Absent (défaut) : la carte seule, comme avant.
     */
    extraAction?: { label: string; iconName: IconName; onPress: () => void }
    /** À qui la carte parle. Défaut `"parent"`. */
    voice?: Voice
    /** La version courte (le fil du live) : l'énoncé coupé, sans compétences. Défaut `false`. */
    compact?: boolean
    testID?: string
}

export function GeneratedActivityCard(props: GeneratedActivityCardProps) {
    const {
        activity,
        quizAvailable = false,
        onToggleQuiz,
        onCreate,
        extraAction,
        voice = "parent",
        compact = false,
        testID,
    } = props

    if (activity.status === "pending") {
        return <GeneratedActivityCardSkeleton variationLabel={activity.variationLabel} testID={testID} />
    }

    if (activity.status === "error") {
        return (
            <Card p="4" testID={testID}>
                <Stack gap="3" align="center">
                    <Icon name="exclamation-triangle" size={24} color="text-danger" />
                    <Text color="text-danger">Erreur de génération</Text>
                    {activity.variationLabel != null && <Badge text={activity.variationLabel} variant="neutral" />}
                </Stack>
            </Card>
        )
    }

    const created = activity.creation === "created"
    const creating = activity.creation === "creating"

    return (
        <Card p="4" background={CARD_SURFACE} testID={testID}>
            <Stack gap="3">
                {(activity.subject != null || activity.chapterName != null) && (
                    <Row gap="2" align="center">
                        {activity.subject != null && <SubjectBadge subject={activity.subject} />}
                        {activity.chapterName != null && (
                            <Text size="text-sm" color="text-muted" numberOfLines={1} style={styles.grow}>
                                {activity.chapterName}
                            </Text>
                        )}
                    </Row>
                )}

                {!compact && activity.keySkills != null && activity.keySkills !== "" && (
                    <Card background="surface-subtle" elevation="none" p="3">
                        <Text size="text-sm" color="text-muted">
                            {activity.keySkills}
                        </Text>
                    </Card>
                )}

                {(activity.levelLabel != null || activity.variationLabel != null) && (
                    <Row gap="2" align="center" style={styles.chips}>
                        {activity.levelLabel != null && <Badge text={activity.levelLabel} variant="info" tinted />}
                        {activity.variationLabel != null && <Badge text={activity.variationLabel} variant="neutral" />}
                    </Row>
                )}

                {activity.title != null && <Title size={compact ? "text-base" : "text-lg"}>{activity.title}</Title>}

                {activity.content != null &&
                    activity.content !== "" &&
                    (compact ? <Excerpt value={activity.content} /> : <RichText value={activity.content} />)}

                {quizAvailable && (
                    <Card background="surface-subtle" elevation="none" p="3" leftBorderColor="primary">
                        <Row gap="3" align="center" justify="between">
                            <Stack gap="0" style={styles.grow}>
                                <Text weight="semibold">Quiz de prérequis</Text>
                                <Text size="text-sm" color="text-muted">
                                    Détecte les lacunes avant l&apos;exercice
                                </Text>
                            </Stack>
                            <Switch
                                checked={activity.quizEnabled === true}
                                onChange={(next) => onToggleQuiz?.(next)}
                                disabled={created}
                            />
                        </Row>
                    </Card>
                )}

                {activity.quizError === true && (
                    <Banner color="danger" tinted icon="exclamation-circle" message={TEXTS[voice].quizError} />
                )}

                {/* Le mot du bouton dit son avancement : `loading` masque le libellé, donc il
                    passe à « Création... » pour que la phrase existe au moins pour un lecteur
                    d'écran, et reparaisse si l'envoi échoue. */}
                <View style={compact ? styles.actions : undefined}>
                    <Button
                        label={created ? "Activité ajoutée" : creating ? "Création..." : "Créer l'activité"}
                        iconName={created ? "check" : "check-circle"}
                        color="success"
                        size={compact ? "sm" : undefined}
                        fullWidth={!compact}
                        loading={creating}
                        disabled={created}
                        onPress={onCreate}
                    />
                    {extraAction != null && (
                        <Button
                            label={extraAction.label}
                            iconName={extraAction.iconName}
                            variant="outline"
                            size="sm"
                            fullWidth={!compact}
                            onPress={extraAction.onPress}
                        />
                    )}
                </View>
            </Stack>
        </Card>
    )
}

/**
 * L'énoncé de la version courte : quelques lignes, de quoi le reconnaître, pas le lire. Coupé à une hauteur fixe,
 * il pourrait trancher une ligne au milieu des lettres (la police de l'énoncé, ses formules, n'ont pas la hauteur de
 * ligne du texte) : quand il dépasse, son bas s'efface vers la carte. Plus court, il reste entier, sans voile.
 */
function Excerpt({ value }: { value: string }) {
    const { theme } = useUnistyles()
    const [overflows, setOverflows] = useState(false)
    const surface = theme.colors[CARD_SURFACE]
    return (
        <View style={styles.excerpt} pointerEvents="none">
            <View onLayout={(event) => setOverflows(event.nativeEvent.layout.height > EXCERPT_HEIGHT)}>
                <RichText value={value} />
            </View>
            {overflows && (
                <View style={styles.fade}>
                    <LinearGradient colors={[hexWithAlpha(surface, 0) ?? "transparent", surface]} style={styles.fill} />
                </View>
            )}
        </View>
    )
}

/** Environ trois lignes de l'énoncé. */
const EXCERPT_HEIGHT = 84
/** Le fondu couvre la dernière ligne visible. */
const FADE_HEIGHT = 32
/** La surface de la carte, vers laquelle l'extrait s'efface. */
const CARD_SURFACE = "surface-raised"

const styles = StyleSheet.create((theme) => ({
    grow: { flexShrink: 1, flexGrow: 1 },
    chips: { flexWrap: "wrap" as const },
    excerpt: { maxHeight: EXCERPT_HEIGHT, overflow: "hidden" as const },
    fade: { position: "absolute" as const, left: 0, right: 0, bottom: 0, height: FADE_HEIGHT },
    fill: { flex: 1 },
    actions: {
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        gap: theme.spacing["2"],
        alignItems: "center" as const,
    },
}))
